import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { fetchApi } from '../../Network/NetworkService';
import { exportAsExcel } from '../../Services/exportAsExcel';
import SimpleChart from '../Ui/SimpleChart';

const formatEuros = (n) =>
    (Number(n) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const Sparkline = ({ series, width = 100, height = 24, color = '#d9534f' }) => {
    if (!series || !series.length) {
        return <span className="text-muted" style={{ fontSize: '11px' }}>—</span>;
    }
    const max = Math.max(...series, 1);
    const stepX = width / Math.max(series.length - 1, 1);
    const points = series.map((v, i) => `${i * stepX},${height - (v / max) * height}`).join(' ');
    const total = series.reduce((s, v) => s + v, 0);
    return (
        <svg width={width} height={height}>
            <title>{`${total} refus sur la période`}</title>
            <polyline
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                points={points}
            />
        </svg>
    );
};

const DoctorBehavior = () => {
    const [data, setData] = useState({ doctors: [], benchmarks: {}, total: {}, minAssigned: 5 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        start: '2026-01-01',
        end: new Date().toISOString().split('T')[0],
        minAssigned: 5
    });
    const [sortConfig, setSortConfig] = useState({ key: 'abandonRate', direction: 'desc' });
    const latestFetchRef = useRef(0);

    const fetchData = useCallback(async () => {
        const fetchId = ++latestFetchRef.current;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                start: filters.start,
                end: filters.end,
                minAssigned: filters.minAssigned
            });
            const res = await fetchApi(`/doctor-cancellations/behavior?${params.toString()}`, 'GET');
            if (fetchId !== latestFetchRef.current) return;
            if (res && res.success) {
                setData(res.data);
            } else {
                setError((res && res.message) || 'Erreur lors du chargement');
            }
        } catch (err) {
            if (fetchId !== latestFetchRef.current) return;
            setError(`Erreur: ${err.message}`);
        } finally {
            if (fetchId === latestFetchRef.current) setLoading(false);
        }
    }, [filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const sortedDoctors = useMemo(() => {
        return [...data.doctors].sort((a, b) => {
            const va = a[sortConfig.key] ?? '';
            const vb = b[sortConfig.key] ?? '';
            if (typeof va === 'string' || typeof vb === 'string') {
                return sortConfig.direction === 'asc'
                    ? String(va).localeCompare(String(vb))
                    : String(vb).localeCompare(String(va));
            }
            const na = Number(va) || 0;
            const nb = Number(vb) || 0;
            return sortConfig.direction === 'asc' ? na - nb : nb - na;
        });
    }, [data.doctors, sortConfig]);

    const distributionChart = useMemo(() => {
        if (!data.doctors.length) return null;
        const top = data.doctors.slice(0, 20);
        return {
            categories: top.map(d => d.doctorName),
            series: [{
                name: "Taux d'abandon (%)",
                data: top.map(d => d.abandonRate)
            }]
        };
    }, [data.doctors]);

    const handleExport = () => {
        const rows = sortedDoctors.map(d => ({
            'Médecin': d.doctorName,
            'Assignées': d.assigned,
            'Terminées': d.completed,
            'Indispo. à distance': d.notOnline,
            'Annulées par docteur': d.canceledByDoctor,
            'Total abandons': d.abandons,
            'Refus bruts': d.refusalPressure || 0,
            "Taux d'abandon (%)": d.abandonRate,
            'Manque à gagner estimé (€)': d.estimatedLost
        }));
        exportAsExcel(rows, 'doctor_behavior');
    };

    const rowColor = (rate) => {
        const { p90, p75, median } = data.benchmarks;
        if (p90 > 0 && rate >= p90) return '#f2dede'; // red bg
        if (p75 > 0 && rate >= p75) return '#fcf8e3'; // yellow bg
        if (median > 0 && rate <= median / 2) return '#dff0d8'; // green bg (good)
        return undefined;
    };

    const sortIcon = (key) => sortConfig.key === key
        ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')
        : '';

    const benchmarks = data.benchmarks || {};
    const total = data.total || {};

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Comportement des médecins</h2>
                    <p className="text-muted">
                        Taux d'abandon par médecin : {' '}
                        <code>(indispo. à distance + annulations) / requêtes assignées</code>.
                        Comparé aux benchmarks de l'équipe pour identifier les profils atypiques.
                        Les médecins en-dessous du seuil de requêtes assignées sont masqués pour
                        éviter le bruit statistique.
                        {' '}
                        <strong>Refus bruts</strong> : nombre de fois où le médecin apparaît dans{' '}
                        <code>refusals[]</code> — compte tous les refus, y compris ceux n'ayant
                        pas fait basculer la requête en "impossible". Actif uniquement quand{' '}
                        <code>FEATURE_REFUSAL_THRESHOLD</code> est activé côté serveur.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Date début</label>
                        <input type="date" className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input type="date" className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label title="Médecins avec moins de N requêtes assignées sont masqués">
                            Min. requêtes assignées
                        </label>
                        <input type="number" min="1" className="form-control"
                            value={filters.minAssigned}
                            onChange={(e) => setFilters({ ...filters, minAssigned: e.target.value })} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button bsStyle="primary" onClick={fetchData} disabled={loading} style={{ marginRight: '10px' }}>
                            {loading ? 'Chargement...' : 'Actualiser'}
                        </Button>
                        <Button bsStyle="success" onClick={handleExport} disabled={!sortedDoctors.length}>
                            Exporter Excel
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Médecins analysés</h5>
                        <h2 style={{ margin: 0 }}>{benchmarks.count || 0}</h2>
                        <small className="text-muted">≥ {data.minAssigned} req. assignées</small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Taux moyen équipe</h5>
                        <h2 style={{ margin: 0 }}>{(benchmarks.mean || 0).toFixed(2)}%</h2>
                        <small className="text-muted">médiane {(benchmarks.median || 0).toFixed(1)}% · p75 {(benchmarks.p75 || 0).toFixed(1)}% · p90 {(benchmarks.p90 || 0).toFixed(1)}%</small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Total abandons</h5>
                        <h2 style={{ color: '#d9534f', margin: 0 }}>{total.abandons || 0}</h2>
                        <small className="text-muted">
                            sur {total.assigned || 0} ({total.abandonRate || 0}%)
                        </small>
                        {total.refusalPressure > 0 && (
                            <small
                                className="text-muted"
                                style={{ display: 'block', marginTop: '4px' }}
                                title="Nombre total d'entrées dans refusals[] sur la période, indépendamment du seuil. Inclut les refus n'ayant pas fait basculer la requête en impossible."
                            >
                                + {total.refusalPressure} refus bruts (non comptés ci-dessus)
                            </small>
                        )}
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5
                            className="text-muted"
                            style={{ marginTop: 0 }}
                            title="Somme des tarifs estimés des requêtes abandonnées (priceForRequest : paidAmount si encaissé, sinon prix de base par type + bonus)."
                        >
                            Manque à gagner estimé
                        </h5>
                        <h2 style={{ color: '#d9534f', margin: 0 }}>{formatEuros(total.estimatedLost)}</h2>
                    </Panel.Body></Panel>
                </Col>
            </Row>

            {distributionChart && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading><h4>Top 20 — taux d'abandon par médecin</h4></Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={400}
                                    categories={distributionChart.categories}
                                    series={distributionChart.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <h4>Tableau des comportements ({sortedDoctors.length} médecins)</h4>
                            <small className="text-muted">
                                Ligne rouge = ≥ p90 équipe ({(benchmarks.p90 || 0).toFixed(1)}%) ·
                                Jaune = ≥ p75 ({(benchmarks.p75 || 0).toFixed(1)}%) ·
                                Verte = ≤ médiane / 2 ({((benchmarks.median || 0) / 2).toFixed(1)}%)
                            </small>
                        </Panel.Heading>
                        <Panel.Body style={{ maxHeight: '700px', overflowY: 'auto' }}>
                            <table className="table table-bordered table-hover">
                                <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                                    <tr>
                                        <th onClick={() => handleSort('doctorName')} style={{ cursor: 'pointer' }}>
                                            Médecin{sortIcon('doctorName')}
                                        </th>
                                        <th onClick={() => handleSort('assigned')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Assignées{sortIcon('assigned')}
                                        </th>
                                        <th onClick={() => handleSort('completed')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Terminées{sortIcon('completed')}
                                        </th>
                                        <th onClick={() => handleSort('notOnline')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Indispo.{sortIcon('notOnline')}
                                        </th>
                                        <th onClick={() => handleSort('canceledByDoctor')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Annul.{sortIcon('canceledByDoctor')}
                                        </th>
                                        <th onClick={() => handleSort('abandons')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Abandons{sortIcon('abandons')}
                                        </th>
                                        <th
                                            onClick={() => handleSort('refusalPressure')}
                                            style={{ cursor: 'pointer', textAlign: 'right' }}
                                            title="Nombre total de refus bruts par ce médecin (entrées dans refusals[] sur toutes les requêtes), même quand le seuil n'a pas été atteint. Vide tant que FEATURE_REFUSAL_THRESHOLD n'est pas activé côté serveur."
                                        >
                                            Refus bruts{sortIcon('refusalPressure')}
                                        </th>
                                        <th
                                            style={{ textAlign: 'center' }}
                                            title="Évolution quotidienne des refus bruts sur la période. Permet de distinguer un médecin qui refuse régulièrement (pression continue) d'un pic ponctuel."
                                        >
                                            Tendance
                                        </th>
                                        <th onClick={() => handleSort('abandonRate')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Taux d'abandon{sortIcon('abandonRate')}
                                        </th>
                                        <th onClick={() => handleSort('estimatedLost')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                            Manque à gagner{sortIcon('estimatedLost')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedDoctors.map(d => (
                                        <tr key={d.doctorId} style={{ background: rowColor(d.abandonRate) }}>
                                            <td>
                                                <Link to={`/doctors/detail/${d.doctorId}`}>{d.doctorName}</Link>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{d.assigned}</td>
                                            <td style={{ textAlign: 'right' }}>{d.completed}</td>
                                            <td style={{ textAlign: 'right' }}>{d.notOnline}</td>
                                            <td style={{ textAlign: 'right' }}>{d.canceledByDoctor}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>{d.abandons}</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {d.refusalPressure > 0 ? (
                                                    <span className="label label-warning">{d.refusalPressure}</span>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <Sparkline series={d.refusalsDailySeries} />
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>{d.abandonRate}%</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatEuros(d.estimatedLost)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default DoctorBehavior;
